import React, { useState, useEffect, useMemo } from "react";
import { 
  FolderPlus, 
  Upload, 
  Search, 
  LayoutGrid, 
  List, 
  Folder, 
  ChevronRight, 
  Home, 
  FileUp, 
  Loader2, 
  Filter, 
  Sparkles,
  ArrowLeft,
  X,
  FileText,
  FolderOpen
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  subscribeToMarketingFolders,
  subscribeToMarketingDocuments,
  createMarketingFolder,
  updateMarketingFolder,
  deleteMarketingFolder,
  uploadMarketingDocument,
  updateMarketingDocument,
  deleteMarketingDocument,
  incrementDocumentDownload
} from "../services/marketingService";
import FolderCard from "../components/marketing/FolderCard";
import DocumentCard from "../components/marketing/DocumentCard";
import CreateFolderModal from "../components/modals/CreateFolderModal";
import UploadDocumentModal from "../components/modals/UploadDocumentModal";
import DocumentDetailsModal from "../components/modals/DocumentDetailsModal";

export default function Marketing() {
  const { currentUser } = useAuth();

  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [viewMode, setViewMode] = useState("list"); // "list" | "grid"

  // Modals
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [uploadDocOpen, setUploadDocOpen] = useState(false);
  const [detailsDocOpen, setDetailsDocOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Subscriptions
  useEffect(() => {
    setLoading(true);
    let foldersLoaded = false;
    let docsLoaded = false;

    const unsubFolders = subscribeToMarketingFolders((list, err) => {
      if (!err) setFolders(list);
      foldersLoaded = true;
      if (docsLoaded) setLoading(false);
    });

    const unsubDocs = subscribeToMarketingDocuments((list, err) => {
      if (!err) setDocuments(list);
      docsLoaded = true;
      if (foldersLoaded) setLoading(false);
    });

    return () => {
      unsubFolders();
      unsubDocs();
    };
  }, []);

  // Compute Active Folder Object
  const currentFolder = useMemo(() => {
    if (!currentFolderId) return null;
    return folders.find(f => f.id === currentFolderId) || null;
  }, [folders, currentFolderId]);

  // Compute Breadcrumbs Trail
  const breadcrumbs = useMemo(() => {
    const trail = [];
    let curr = currentFolder;
    while (curr) {
      trail.unshift(curr);
      curr = curr.parentId ? folders.find(f => f.id === curr.parentId) : null;
    }
    return trail;
  }, [folders, currentFolder]);

  // Folder Item Counts (subfolders + docs directly in that folder)
  const folderItemCounts = useMemo(() => {
    const counts = {};
    for (const f of folders) {
      const childFolders = folders.filter(child => child.parentId === f.id).length;
      const childDocs = documents.filter(doc => doc.folderId === f.id).length;
      counts[f.id] = childFolders + childDocs;
    }
    return counts;
  }, [folders, documents]);

  // Visible Folders (filtered by current location and search)
  const visibleFolders = useMemo(() => {
    if (searchTerm.trim()) {
      // If searching, search across all folders
      const term = searchTerm.toLowerCase();
      return folders.filter(f => f.name.toLowerCase().includes(term));
    }
    return folders.filter(f => f.parentId === currentFolderId);
  }, [folders, currentFolderId, searchTerm]);

  // Visible Documents (filtered by location, search, and type)
  const visibleDocuments = useMemo(() => {
    let list = documents;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(d => 
        (d.title || "").toLowerCase().includes(term) ||
        (d.originalFileName || "").toLowerCase().includes(term) ||
        (d.description || "").toLowerCase().includes(term) ||
        (d.tags || []).some(t => t.toLowerCase().includes(term))
      );
    } else {
      list = list.filter(d => d.folderId === currentFolderId);
    }

    if (typeFilter !== "All") {
      list = list.filter(d => {
        if (typeFilter === "PDF") return d.fileType === "pdf";
        if (typeFilter === "Presentations") return d.fileType === "presentation";
        if (typeFilter === "Documents") return d.fileType === "document";
        if (typeFilter === "Spreadsheets") return d.fileType === "spreadsheet";
        if (typeFilter === "Images") return d.fileType === "image";
        if (typeFilter === "Videos") return d.fileType === "video";
        if (typeFilter === "Archives") return d.fileType === "archive";
        return true;
      });
    }

    return list;
  }, [documents, currentFolderId, searchTerm, typeFilter]);

  // Handlers
  const handleCreateFolder = async (folderData) => {
    setActionLoading(true);
    try {
      await createMarketingFolder({
        ...folderData,
        createdBy: currentUser?.displayName || currentUser?.email?.split("@")[0] || "User"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditFolder = async (folder) => {
    const newName = window.prompt("Enter new folder name:", folder.name);
    if (!newName || !newName.trim() || newName.trim() === folder.name) return;
    try {
      await updateMarketingFolder(folder.id, { name: newName.trim() });
    } catch (err) {
      console.error("Error renaming folder:", err);
      alert("Failed to rename folder.");
    }
  };

  const handleDeleteFolder = async (folder) => {
    const count = folderItemCounts[folder.id] || 0;
    const confirmMsg = count > 0
      ? `Folder "${folder.name}" contains ${count} item(s). Deleting it will permanently remove all subfolders and documents inside it. Are you sure?`
      : `Are you sure you want to delete folder "${folder.name}"?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      await deleteMarketingFolder(folder.id, folders, documents);
      if (currentFolderId === folder.id) {
        setCurrentFolderId(folder.parentId || null);
      }
    } catch (err) {
      console.error("Error deleting folder:", err);
      alert("Failed to delete folder.");
    }
  };

  const handleUploadDocument = async (uploadData) => {
    setActionLoading(true);
    try {
      await uploadMarketingDocument({
        ...uploadData,
        uploadedBy: currentUser?.displayName || currentUser?.email?.split("@")[0] || "User"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateDoc = async (docId, data) => {
    await updateMarketingDocument(docId, data);
  };

  const handleDeleteDoc = async (docId, storagePath) => {
    await deleteMarketingDocument(docId, storagePath);
  };

  const handleDownload = async (docItem) => {
    if (!docItem.downloadUrl) return;
    incrementDocumentDownload(docItem.id);
    
    // Open download link in new tab or trigger download
    const link = document.createElement("a");
    link.href = docItem.downloadUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = docItem.originalFileName || "document";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="animate-fade-in" style={{ paddingBottom: "3rem" }}>
      {/* Page Header */}
      <div className="page-header" style={{ marginBottom: "1.5rem" }}>
        <div className="page-header-info">
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <div 
              style={{ 
                width: "40px", 
                height: "40px", 
                borderRadius: "10px", 
                backgroundColor: "var(--primary-light)", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                color: "var(--primary)"
              }}
            >
              <FolderOpen size={22} />
            </div>
            <div>
              <h1>Marketing & Collateral</h1>
              <p>Organize, search, and download sales presentations, brochures, case studies, and brand assets.</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setCreateFolderOpen(true)}
            style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
          >
            <FolderPlus size={16} />
            New Folder
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setUploadDocOpen(true)}
            style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
          >
            <Upload size={16} />
            Upload Document
          </button>
        </div>
      </div>

      {/* Breadcrumbs & Navigation Bar */}
      <div 
        className="card glass-card"
        style={{ 
          padding: "0.75rem 1.25rem", 
          marginBottom: "1.5rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem"
        }}
      >
        {/* Breadcrumb Trail */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => setCurrentFolderId(null)}
            style={{
              background: "none",
              border: "none",
              color: currentFolderId === null ? "var(--primary)" : "var(--text-secondary)",
              fontWeight: currentFolderId === null ? 700 : 500,
              fontSize: "0.9rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.35rem",
              padding: "0.2rem 0.4rem",
              borderRadius: "4px"
            }}
          >
            <Home size={16} />
            Marketing Library
          </button>

          {breadcrumbs.map((f, idx) => {
            const isLast = idx === breadcrumbs.length - 1;
            return (
              <React.Fragment key={f.id}>
                <ChevronRight size={14} style={{ color: "var(--text-muted)" }} />
                <button
                  type="button"
                  onClick={() => setCurrentFolderId(f.id)}
                  style={{
                    background: "none",
                    border: "none",
                    color: isLast ? "var(--primary)" : "var(--text-secondary)",
                    fontWeight: isLast ? 700 : 500,
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.35rem",
                    padding: "0.2rem 0.4rem",
                    borderRadius: "4px"
                  }}
                >
                  <Folder size={15} style={{ color: f.color || "var(--primary)" }} />
                  {f.name}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {/* View Mode Switcher */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
          <button
            type="button"
            className={`btn-icon ${viewMode === "grid" ? "active" : ""}`}
            onClick={() => setViewMode("grid")}
            title="Grid View"
            style={{
              background: viewMode === "grid" ? "var(--primary-light)" : "transparent",
              color: viewMode === "grid" ? "var(--primary)" : "var(--text-secondary)",
              border: "1px solid var(--border-color)",
              borderRadius: "6px",
              padding: "0.4rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center"
            }}
          >
            <LayoutGrid size={16} />
          </button>
          <button
            type="button"
            className={`btn-icon ${viewMode === "list" ? "active" : ""}`}
            onClick={() => setViewMode("list")}
            title="List View"
            style={{
              background: viewMode === "list" ? "var(--primary-light)" : "transparent",
              color: viewMode === "list" ? "var(--primary)" : "var(--text-secondary)",
              border: "1px solid var(--border-color)",
              borderRadius: "6px",
              padding: "0.4rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center"
            }}
          >
            <List size={16} />
          </button>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="table-actions-container" style={{ marginBottom: "1.5rem" }}>
        <div className="search-box-container">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            className="form-input search-input"
            placeholder="Search marketing materials by title, keyword tags, filename, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              style={{
                position: "absolute",
                right: "1rem",
                background: "none",
                border: "none",
                color: "var(--text-muted)",
                cursor: "pointer"
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="filter-select-group">
          <select
            className="form-input filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{ minWidth: "160px" }}
          >
            <option value="All">All Formats</option>
            <option value="PDF">PDF Documents</option>
            <option value="Presentations">Pitch Decks (PPT)</option>
            <option value="Documents">Word Documents</option>
            <option value="Spreadsheets">Spreadsheets (Excel)</option>
            <option value="Images">Images & Graphics</option>
            <option value="Videos">Videos</option>
            <option value="Archives">ZIP Archives</option>
          </select>
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div className="loading-container">
          <Loader2 className="spinner-icon" size={36} />
          <span>Loading marketing library...</span>
        </div>
      ) : (
        <div>
          {/* Folders Section (Shown when folders exist in this directory or matched by search) */}
          {visibleFolders.length > 0 && (
            <div style={{ marginBottom: "2rem" }}>
              <h3 
                style={{ 
                  fontSize: "1rem", 
                  fontWeight: 600, 
                  color: "var(--text-secondary)", 
                  marginBottom: "1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem"
                }}
              >
                <Folder size={18} style={{ color: "var(--primary)" }} />
                Folders ({visibleFolders.length})
              </h3>
              <div 
                style={{ 
                  display: "grid", 
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", 
                  gap: "1rem" 
                }}
              >
                {visibleFolders.map((f) => (
                  <FolderCard
                    key={f.id}
                    folder={f}
                    itemCount={folderItemCounts[f.id] || 0}
                    onClick={(folder) => {
                      setSearchTerm("");
                      setCurrentFolderId(folder.id);
                    }}
                    onEdit={handleEditFolder}
                    onDelete={handleDeleteFolder}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Documents Section */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 
                style={{ 
                  fontSize: "1rem", 
                  fontWeight: 600, 
                  color: "var(--text-secondary)", 
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem"
                }}
              >
                <FileText size={18} style={{ color: "var(--primary)" }} />
                Documents ({visibleDocuments.length})
              </h3>
            </div>

            {visibleDocuments.length === 0 && visibleFolders.length === 0 ? (
              <div className="empty-state-card" style={{ padding: "4rem 2rem" }}>
                <FileUp size={48} className="empty-state-icon" style={{ opacity: 0.4 }} />
                <h3>No Marketing Materials Found</h3>
                <p>
                  {searchTerm
                    ? `No files or folders matched "${searchTerm}". Try adjusting your search.`
                    : "This folder is empty. Upload sales collateral or create subfolders to get started."}
                </p>
                <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", marginTop: "1.5rem" }}>
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={() => setUploadDocOpen(true)}
                  >
                    <Upload size={16} /> Upload First Document
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setCreateFolderOpen(true)}
                  >
                    <FolderPlus size={16} /> Create Folder
                  </button>
                </div>
              </div>
            ) : visibleDocuments.length === 0 ? (
              <div 
                style={{ 
                  textAlign: "center", 
                  padding: "2.5rem 1rem", 
                  backgroundColor: "var(--bg-input)", 
                  borderRadius: "8px",
                  border: "1px dashed var(--border-color)",
                  color: "var(--text-secondary)",
                  fontSize: "0.9rem"
                }}
              >
                No documents directly in this view. Use the button above to upload files.
              </div>
            ) : viewMode === "grid" ? (
              <div 
                style={{ 
                  display: "grid", 
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", 
                  gap: "1.25rem" 
                }}
              >
                {visibleDocuments.map((docItem) => (
                  <DocumentCard
                    key={docItem.id}
                    document={docItem}
                    viewMode="grid"
                    onClick={(doc) => {
                      setSelectedDoc(doc);
                      setDetailsDocOpen(true);
                    }}
                    onDownload={handleDownload}
                    onDelete={handleDeleteDoc}
                  />
                ))}
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Format</th>
                      <th>Size</th>
                      <th>Folder</th>
                      <th>Tags</th>
                      <th>Uploaded By</th>
                      <th>Date</th>
                      <th style={{ textAlign: "center" }}>Downloads</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleDocuments.map((docItem) => (
                      <DocumentCard
                        key={docItem.id}
                        document={docItem}
                        viewMode="list"
                        folderName={folders.find(f => f.id === docItem.folderId)?.name || "Root"}
                        onClick={(doc) => {
                          setSelectedDoc(doc);
                          setDetailsDocOpen(true);
                        }}
                        onDownload={handleDownload}
                        onDelete={handleDeleteDoc}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      <CreateFolderModal
        isOpen={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        currentFolder={currentFolder}
        onSubmit={handleCreateFolder}
        loading={actionLoading}
      />

      <UploadDocumentModal
        isOpen={uploadDocOpen}
        onClose={() => setUploadDocOpen(false)}
        currentFolder={currentFolder}
        allFolders={folders}
        onSubmit={handleUploadDocument}
        loading={actionLoading}
      />

      <DocumentDetailsModal
        isOpen={detailsDocOpen}
        onClose={() => {
          setDetailsDocOpen(false);
          setSelectedDoc(null);
        }}
        document={selectedDoc}
        allFolders={folders}
        onUpdate={handleUpdateDoc}
        onDelete={handleDeleteDoc}
        onDownload={handleDownload}
        loading={actionLoading}
      />
    </div>
  );
}
